import React, { useCallback, useEffect, useState } from 'react';
import { ChartLayout, EquityContent, MethodologyContent, OverviewContent, RelevanceContent } from '../charts/ChartLayout';
import { ChartRendererSupabase } from '../charts/ChartRendererSupabase';
import fetchEducationalAttainmentByRaceSupabase from '../charts/EducationalAttainmentByRaceSupabase';
import fetchEducationalAttainmentOverTimeSupabase from '../charts/EducationalAttainmentOverTimeSupabase';
import { KeyMetric, KeyMetrics } from '../charts/KeyMetrics';
import fetchPercentageNoHealthInsuranceSupabase from '../charts/PercentageNoHealthInsuranceSupabase';
import { PageHeader } from '../layout/PageHeader';
import PercentageNoHealthInsuranceRowSupabase from '../rows/PercentageNoHealthInsuranceRowSupabase';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
// Import all chart components

import { HousingToEmployees } from '../charts/HousingToEmployees';
import PovertyByRaceTrends from '../charts/PovertyByRaceTrends';
import IncomeByRaceTrends from '../charts/IncomeByRaceTrends';
import IncomeByRaceBar from '../charts/IncomeByRaceBar';
import PovertyByRaceBar from '../charts/PovertyByRaceBar';
import GenericMapTiered from '../charts/GenericMapTiered';
import { CustomSectionsForPage } from '../customContent/CustomSectionsForPage';
import { CustomDatasetsForPage } from '../customContent/CustomDatasetsForPage';
import { useLocation } from '../../contexts/LocationContext';
import { useAdminPageSettings } from '../../hooks/useAdminPageSettings';
import JobWagesBySector from '../charts/JobWagesBySector';
import JobWagesBySectorTrends from '../charts/JobWagesBySectorTrends';
import JobWagesBySectorComparison from '../charts/JobWagesBySectorComparison';
import JobWageMedianTrendComparison from '../charts/JobWageMedianTrendComparison';
import EmploymentSection from '../charts/EmploymentSection';
import PopulationTrendsSection from '../charts/PopulationTrendsSection';
import HousingBurdenSection from '../charts/HousingBurdenSection';
import HomelessnessSection from '../charts/HomelessnessSection';
import CrimeSafetySection from '../charts/CrimeSafetySection';
import { HealthAsthmaChart } from '../charts/HealthAsthmaChart';
import { HealthCardiovascularChart } from '../charts/HealthCardiovascularChart';
import { HealthLowBirthWeightChart } from '../charts/HealthLowBirthWeightChart';
import { CashPublicAssistanceChart } from '../charts/CashPublicAssistanceChart';
import { NeighborhoodOpportunityChart } from '../charts/NeighborhoodOpportunityChart';
import SectionWrapper, { useSectionReveal } from './SectionWrapper';

import housingToEmployeesBg from '../../assets/people/housing-to-employees-bg.jpg';
import populationTrendsBg from '../../assets/people/population-trends-bg.jpg';
import employmentBg from '../../assets/people/employment-bg.jpg';
import housingBurdenBg from '../../assets/people/housing-burden-bg.jpg';
import publicAssistanceBg from '../../assets/people/public-assistance-bg.jpg';
import neighborhoodOpportunityBg from '../../assets/people/neighborhood-opportunity-bg.jpg';
import homelessnessBg from '../../assets/people/homelessness-bg.jpg';
import healthIndicatorsBg from '../../assets/people/health-indicators-bg.jpg';
import educationalAttainmentBg from '../../assets/people/educational-attainment-bg.jpg';
import incomePovertyBg from '../../assets/people/income-poverty-bg.jpg';
import jobWagesBg from '../../assets/people/job-wages-bg.jpg';
import peopleMapsBg from '../../assets/people/people-maps-bg.jpg';

const PEOPLE_SECTION_BG: Record<string, string> = {
  housing_to_employees: housingToEmployeesBg,
  population_trends: populationTrendsBg,
  employment: employmentBg,
  housing_burden: housingBurdenBg,
  public_assistance: publicAssistanceBg,
  neighborhood_opportunity: neighborhoodOpportunityBg,
  homelessness: homelessnessBg,
  health_indicators: healthIndicatorsBg,
  educational_attainment: educationalAttainmentBg,
  income_poverty: incomePovertyBg,
  job_wages: jobWagesBg,
  people_maps: peopleMapsBg,
};

const PEOPLE_SECTION_IDS = Object.keys(PEOPLE_SECTION_BG);

// --- Extracted interfaces ---

interface HealthIndicatorMetricsState {
  asthma: { location: number | null; ca: number | null };
  cardiovascular_disease: { location: number | null; ca: number | null };
  low_birth_weight: { location: number | null; ca: number | null };
}

interface IncomePovertyMetricsState {
  incomeRatio: number | null;
  povertyRatio: number | null;
  highestPovertyRate: number | null;
  lowestIncome: number | null;
}

interface JobWagesMetricsState {
  highestWage: number | null;
  lowestWage: number | null;
  wageGapRatio: number | null;
  sectorsAboveCA: number | null;
  latestYear: number | null;
}

interface MapMetricsState {
  currentValue: number;
  californiaAverage: number;
  year: number;
  variable: string;
}

// --- Extracted helpers ---

function filterAndSortOptions<T extends { id: string }>(
  options: T[],
  sectionId: string,
  isChartEnabled: (section: string, chart: string) => boolean,
  getChartOrder: (section: string, chart: string) => number
): T[] {
  return options
    .filter((option) => isChartEnabled(sectionId, option.id))
    .sort((a, b) => getChartOrder(sectionId, a.id) - getChartOrder(sectionId, b.id));
}

function useSyncedChartOption(
  allowedOptions: { value: string }[],
  currentValue: string,
  setValue: (v: any) => void
): void {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (allowedOptions.length && !allowedOptions.some((opt) => opt.value === currentValue)) {
      setValue(allowedOptions[0].value);
    }
  }, [allowedOptions, currentValue, setValue]);
}

function downloadCanvasAsPng(selector: string, filename: string): void {
  const canvas = document.querySelector(`${selector} canvas`) as HTMLCanvasElement;
  if (!canvas) return;
  const link = document.createElement('a');
  link.download = `${filename}.png`;
  link.href = canvas.toDataURL();
  link.click();
}

function formatPeopleMapMetric(variable: string, value: number): string {
  if (variable === 'income') return `$${Math.round(value).toLocaleString()}`;
  if (variable === 'political_lean') {
    if (value > 0) return `D +${value.toFixed(1)}`;
    if (value < 0) return `R +${Math.abs(value).toFixed(1)}`;
    return 'Even';
  }
  if (variable === 'population_density') return `${Math.round(value).toLocaleString()} per sq mi`;
  return `${Math.round(value).toLocaleString()}`;
}

const HealthIndicatorKeyMetrics: React.FC<{
  metrics: { location: number | null; ca: number | null };
  locationLabel: string;
}> = ({ metrics, locationLabel }) => (
  <KeyMetrics>
    {metrics.location != null && (
      <KeyMetric value={metrics.location.toFixed(2)} label={locationLabel} color="#1e3a8a" />
    )}
    {metrics.ca != null && (
      <KeyMetric value={metrics.ca.toFixed(2)} label="California average" color="#059669" />
    )}
    {metrics.location != null && metrics.ca != null && (
      <KeyMetric
        value={metrics.location > metrics.ca ? 'Above' : 'Below'}
        label="vs. California"
        color={metrics.location > metrics.ca ? '#dc2626' : '#10b981'}
      />
    )}
  </KeyMetrics>
);

const HealthIndicatorOverview: React.FC<{
  metrics: { location: number | null; ca: number | null };
  location: string;
  indicatorName: string;
  valueSuffix?: string;
  locationValueSuffix?: string;
  explanation: string;
}> = ({ metrics, location, indicatorName, valueSuffix = '', locationValueSuffix, explanation }) => (
  <OverviewContent
    title="Health Indicators Overview"
    description="Health indicators from CalEnviroScreen 4.0 provide insights into environmental health burdens affecting communities."
  >
    <p>
      {metrics.location != null && metrics.ca != null ? (
        <>In {location}, the {indicatorName} is {metrics.location.toFixed(2)}{locationValueSuffix ?? valueSuffix}, compared to the California average of {metrics.ca.toFixed(2)}{valueSuffix}—{metrics.location > metrics.ca ? 'above' : 'below'} the state average. </>
      ) : metrics.location != null ? (
        <>The {indicatorName} for {location} is {metrics.location.toFixed(2)}{locationValueSuffix ?? valueSuffix}. </>
      ) : null}
      {explanation}
    </p>
  </OverviewContent>
);

interface PeopleProps {
  location?: string;
}

export const People: React.FC<PeopleProps> = ({ location: propLocation }) => {
  const { selectedLocation, selectedGeofips } = useLocation();
  const [chartData, setChartData] = useState<any>(null);
  const [educationalChartData, setEducationalChartData] = useState<any>(null);
  const [educationalTrendsData, setEducationalTrendsData] = useState<any>(null);
  const [rowData, setRowData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeEducationalView, setActiveEducationalView] = useState<string>('overview');
  const [educationalAttainmentView, setEducationalAttainmentView] = useState<'chart' | 'trends' | 'map'>('trends');
  const [activeIncomeView, setActiveIncomeView] = useState<string>('overview');
  const [activeIncomeRaceChart, setActiveIncomeRaceChart] = useState<string>('income-bar');
  const [activeJobWagesView, setActiveJobWagesView] = useState<string>('current'); // chart variant
  const [activeJobWagesNarrative, setActiveJobWagesNarrative] =
    useState<'overview' | 'relevance' | 'equity' | 'methodology'>('overview'); // narrative panel
  const [activeHealthView, setActiveHealthView] = useState<string>('overview');
  const [activeHealthChart, setActiveHealthChart] = useState<'asthma' | 'cardiovascular' | 'lowBirthWeight' | 'health-insurance'>('health-insurance');
  const [healthIndicatorMetrics, setHealthIndicatorMetrics] = useState<HealthIndicatorMetricsState>({ asthma: { location: null, ca: null }, cardiovascular_disease: { location: null, ca: null }, low_birth_weight: { location: null, ca: null } });
  const [incomePovertyMetrics, setIncomePovertyMetrics] = useState<IncomePovertyMetricsState>({
    incomeRatio: null,
    povertyRatio: null,
    highestPovertyRate: null,
    lowestIncome: null,
  });
  const [jobWagesMetrics, setJobWagesMetrics] = useState<JobWagesMetricsState>({
    highestWage: null,
    lowestWage: null,
    wageGapRatio: null,
    sectorsAboveCA: null,
    latestYear: null,
  });
  const [educationalAttainmentMetrics, setEducationalAttainmentMetrics] = useState<MapMetricsState | null>(null);
  const [peopleMapMetrics, setPeopleMapMetrics] = useState<MapMetricsState | null>(null);
  const [activePeopleMapView, setActivePeopleMapView] = useState<'overview' | 'relevance' | 'equity' | 'methodology'>('overview');

  const currentLocation = propLocation || selectedLocation;
  const {
    isSectionEnabled,
    isChartEnabled,
    getSectionOrder,
    getChartOrder
  } = useAdminPageSettings('people', currentLocation);

  const { visible: sectionVisible, refs: sectionRefs } = useSectionReveal(
    PEOPLE_SECTION_IDS,
    !loading && !!currentLocation
  );

  const getSectionStyle = (sectionId: string) => ({
    order: getSectionOrder(sectionId),
    display: isSectionEnabled(sectionId) ? 'block' : 'none'
  });

  // Scroll to chart section when opening page with a hash (e.g. from livability/equity index).
  // Run only when main content is shown (!loading && !error) so the target section exists in the DOM.
  useEffect(() => {
    const scrollToHash = () => {
      const hash = window.location.hash.slice(1);
      if (!hash) return;
      const el = document.getElementById(hash);
      if (!el) return;

      // Center the chart in the viewport so it's visible (not above or below the fold).
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };
    if (loading || error) return;
    const hash = window.location.hash.slice(1);
    if (!hash) return;

    // Three attempts: 120ms (fast layout), 420ms (most charts), 900ms (charts with own async fetch like CrimeSafetySection).
    const t1 = setTimeout(scrollToHash, 120);
    const t2 = setTimeout(scrollToHash, 420);
    const t3 = setTimeout(scrollToHash, 900);
    const onHashChange = () => {
      if (!loading && !error) {
        setTimeout(scrollToHash, 80);
        setTimeout(scrollToHash, 320);
        setTimeout(scrollToHash, 750);
      }
    };
    window.addEventListener('hashchange', onHashChange);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      window.removeEventListener('hashchange', onHashChange);
    };
  }, [loading, error]);

  const healthOptions = [
    { id: 'health_insurance', value: 'health-insurance', label: 'Health Insurance' },
    { id: 'health_asthma', value: 'asthma', label: 'Asthma' },
    { id: 'health_cardiovascular', value: 'cardiovascular', label: 'Cardiovascular Disease' },
    { id: 'health_low_birth_weight', value: 'lowBirthWeight', label: 'Low Birth Weight' }
  ];
  const allowedHealthOptions = filterAndSortOptions(healthOptions, 'health_indicators', isChartEnabled, getChartOrder);

  const educationOptions = [
    { id: 'education_trends', value: 'trends', label: 'Over Time' },
    { id: 'education_chart', value: 'chart', label: 'By Race' },
    { id: 'education_map', value: 'map', label: 'Map' }
  ];
  const allowedEducationOptions = filterAndSortOptions(educationOptions, 'educational_attainment', isChartEnabled, getChartOrder);

  const incomeOptions = [
    { id: 'income_trends', value: 'income-trends', label: 'Income Trends' },
    { id: 'poverty_trends', value: 'poverty-trends', label: 'Poverty Trends' },
    { id: 'income_by_race', value: 'income-bar', label: 'Income (Latest)' },
    { id: 'poverty_bar', value: 'poverty-bar', label: 'Poverty (Latest)' }
  ];
  const allowedIncomeOptions = filterAndSortOptions(incomeOptions, 'income_poverty_race', isChartEnabled, getChartOrder);

  const jobWagesOptions = [
    { id: 'job_wages_current', value: 'current', label: 'Current Wages' },
    { id: 'job_wages_trends', value: 'trends', label: 'Wage Trends' },
    { id: 'job_wages_comparison', value: 'comparison', label: 'vs California' },
    { id: 'job_wages_median_trend', value: 'median-trend', label: 'Median vs CA/County' }
  ];
  const allowedJobWagesOptions = filterAndSortOptions(jobWagesOptions, 'economic_sector_wages', isChartEnabled, getChartOrder);

  const peopleMapVariables = [
    { id: 'income', value: 'income' },
    { id: 'political_lean', value: 'political_lean' },
    { id: 'population_density', value: 'population_density' }
  ];
  const allowedPeopleMapVariables = filterAndSortOptions(peopleMapVariables, 'people_maps', isChartEnabled, getChartOrder)
    .map((option) => option.value);
  const peopleMapsEnabled = isSectionEnabled('people_maps') && allowedPeopleMapVariables.length > 0;
  const homelessnessSectionEnabled = isSectionEnabled('homelessness');
  const crimeSafetySectionEnabled = isSectionEnabled('crime_safety');


  useSyncedChartOption(allowedHealthOptions, activeHealthChart, setActiveHealthChart);
  useSyncedChartOption(allowedEducationOptions, educationalAttainmentView, setEducationalAttainmentView);
  useSyncedChartOption(allowedIncomeOptions, activeIncomeRaceChart, setActiveIncomeRaceChart);
  useSyncedChartOption(allowedJobWagesOptions, activeJobWagesView, setActiveJobWagesView);

  useEffect(() => {
    if (!currentLocation) return;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load health insurance chart data
        const chartResult = await fetchPercentageNoHealthInsuranceSupabase(currentLocation);
        setChartData(chartResult);

        // Load educational attainment chart data (by race and over time)
        const [educationalChartResult, educationalTrendsResult] = await Promise.all([
          fetchEducationalAttainmentByRaceSupabase(currentLocation),
          fetchEducationalAttainmentOverTimeSupabase(currentLocation)
        ]);
        setEducationalChartData(educationalChartResult);
        setEducationalTrendsData(educationalTrendsResult);

        // Load row data
        const rowResult = await PercentageNoHealthInsuranceRowSupabase(currentLocation);
        setRowData(rowResult);

      } catch (err) {
        console.error('Error loading data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentLocation]);

  // Load health indicator metrics (CalEnviroScreen) for key metrics and narrative
  useEffect(() => {
    if (!currentLocation) return;

    const loadHealthIndicatorMetrics = async () => {
      try {
        await supabaseLocationService.initialize();
        const geofips = await supabaseLocationService.getGeofips(currentLocation);
        if (!geofips) return;

        const raw = String(geofips).trim();
        const unpadded = raw.replace(/^0+/, '') || '0';
        const geofipsVariants: (string | number)[] = [raw, unpadded, unpadded.padStart(7, '0')];
        const numeric = Number(unpadded);
        if (!Number.isNaN(numeric)) geofipsVariants.push(numeric);

        const { data: popRow } = await supabaseClient
          .from('population')
          .select('geography_type')
          .in('geofips', geofipsVariants)
          .limit(1)
          .maybeSingle();
        const geographyType = (popRow as { geography_type?: string } | null)?.geography_type || 'City';

        let locData: { asthma?: number; cardiovascular_disease?: number; low_birth_weight?: number } | null = null;
        const { data: firstTry, error: locErr } = await supabaseClient
          .from('calenviroscreen_4_0_aggregated')
          .select('asthma, cardiovascular_disease, low_birth_weight')
          .in('geofips', geofipsVariants)
          .eq('geography_type', geographyType)
          .limit(1)
          .maybeSingle();
        locData = firstTry as typeof locData;

        if (locErr || !locData) {
          for (const geoType of ['City', 'Place', 'County']) {
            if (geoType === geographyType) continue;
            const { data: alt } = await supabaseClient
              .from('calenviroscreen_4_0_aggregated')
              .select('asthma, cardiovascular_disease, low_birth_weight')
              .in('geofips', geofipsVariants)
              .eq('geography_type', geoType)
              .limit(1)
              .maybeSingle();
            if (alt) {
              const a = alt as { asthma?: number; cardiovascular_disease?: number; low_birth_weight?: number };
              setHealthIndicatorMetrics({
                asthma: { location: a.asthma ?? null, ca: null },
                cardiovascular_disease: { location: a.cardiovascular_disease ?? null, ca: null },
                low_birth_weight: { location: a.low_birth_weight ?? null, ca: null },
              });
              return;
            }
          }
          return;
        }

        const { data: caData } = await supabaseClient
          .from('calenviroscreen_4_0_aggregated')
          .select('asthma, cardiovascular_disease, low_birth_weight')
          .in('geofips', [6, '6', '0000006'])
          .eq('geography_type', 'State')
          .limit(1)
          .maybeSingle();

        type HealthRow = { asthma?: number; cardiovascular_disease?: number; low_birth_weight?: number };
        const loc: HealthRow = locData;
        const ca: HealthRow | null = (caData as HealthRow | null) ?? null;
        setHealthIndicatorMetrics({
          asthma: { location: loc.asthma ?? null, ca: ca?.asthma ?? null },
          cardiovascular_disease: { location: loc.cardiovascular_disease ?? null, ca: ca?.cardiovascular_disease ?? null },
          low_birth_weight: { location: loc.low_birth_weight ?? null, ca: ca?.low_birth_weight ?? null },
        });
      } catch (e) {
        // silently ignore
      }
    };

    loadHealthIndicatorMetrics();
  }, [currentLocation]);

  // Fetch and calculate Income/Poverty metrics
  useEffect(() => {
    if (!currentLocation) return;

    const loadIncomePovertyMetrics = async () => {
      try {
        await supabaseLocationService.initialize();
        const geofips = await supabaseLocationService.getGeofips(currentLocation);
        if (!geofips) return;

        const rawGeoid = String(geofips).trim();
        const unpaddedGeoid = rawGeoid.replace(/^0+/, '') || '0';
        const geoidVariants = Array.from(
          new Set([
            rawGeoid,
            unpaddedGeoid,
            unpaddedGeoid.padStart(7, '0'),
            unpaddedGeoid.padStart(5, '0'),
            unpaddedGeoid.padStart(2, '0'),
          ])
        );

        // Find latest year for both tables
        const { data: incomeYears } = await supabaseClient
          .from('median_income_by_race')
          .select('year')
          .in('geoid', geoidVariants)
          .order('year', { ascending: false })
          .limit(1);

        const { data: povertyYears } = await supabaseClient
          .from('poverty_by_race')
          .select('year')
          .in('geoid', geoidVariants)
          .order('year', { ascending: false })
          .limit(1);

        const latestYear = Math.min(
          incomeYears && incomeYears.length > 0 ? Number(incomeYears[0].year) : 9999,
          povertyYears && povertyYears.length > 0 ? Number(povertyYears[0].year) : 9999
        );

        if (latestYear === 9999) return;

        // Fetch income data for latest year
        const { data: incomeData } = await supabaseClient
          .from('median_income_by_race')
          .select('race_or_ethnicity, median_income')
          .in('geoid', geoidVariants)
          .eq('year', latestYear);

        // Fetch poverty data for latest year
        const { data: povertyData } = await supabaseClient
          .from('poverty_by_race')
          .select('race, percentage')
          .in('geoid', geoidVariants)
          .eq('year', latestYear);

        if (!incomeData || incomeData.length === 0 || !povertyData || povertyData.length === 0) return;

        // Calculate income metrics
        const incomeValues = incomeData
          .map((r: any) => Number(r.median_income || 0))
          .filter((v: number) => v > 0);
        
        let incomeRatio = null;
        let lowestIncome = null;
        if (incomeValues.length > 0) {
          const highestIncome = Math.max(...incomeValues);
          lowestIncome = Math.min(...incomeValues);
          incomeRatio = highestIncome / lowestIncome;
        }

        // Calculate poverty metrics
        const povertyValues = povertyData
          .map((r: any) => Number(r.percentage || 0))
          .filter((v: number) => v > 0);
        
        let povertyRatio = null;
        let highestPovertyRate = null;
        if (povertyValues.length > 0) {
          highestPovertyRate = Math.max(...povertyValues);
          const lowestPovertyRate = Math.min(...povertyValues);
          povertyRatio = highestPovertyRate / lowestPovertyRate;
        }

        setIncomePovertyMetrics({
          incomeRatio,
          povertyRatio,
          highestPovertyRate,
          lowestIncome,
        });
      } catch (err) {
        console.error('Error loading income/poverty metrics:', err);
      }
    };

    loadIncomePovertyMetrics();
  }, [currentLocation]);

  // Fetch and calculate Job Wages metrics
  useEffect(() => {
    if (!currentLocation) return;

    const loadJobWagesMetrics = async () => {
      try {
        await supabaseLocationService.initialize();
        const geofips = await supabaseLocationService.getGeofips(currentLocation);
        if (!geofips) {
          return;
        }

        // Main sectors
        const MAIN_SECTORS = [
          'Management, business, science, and arts occupations',
          'Service occupations',
          'Sales and office occupations',
          'Natural resources, construction, and maintenance occupations',
          'Production, transportation, and material moving occupations'
        ];

        // Find latest year - use geofips (numeric) FIRST - this is the primary column
        let { data: years, error: yearsError } = await supabaseClient
          .from('job_by_wage_level')
          .select('year, geofips, location_name')
          .eq('geofips', geofips)
          .order('year', { ascending: false })
          .limit(1);

        // If no data by geofips, try geoid (text) as fallback
        if ((!years || years.length === 0) && !yearsError) {
          const geoid = String(geofips);
          const geoidResult = await supabaseClient
            .from('job_by_wage_level')
            .select('year, geofips, location_name')
            .eq('geoid', geoid)
            .order('year', { ascending: false })
            .limit(1);
          
          if (geoidResult.data && geoidResult.data.length > 0) {
            years = geoidResult.data;
            yearsError = geoidResult.error;
          }
        }

        // If still no data, try location_name
        if ((!years || years.length === 0) && !yearsError) {
          const nameResult = await supabaseClient
            .from('job_by_wage_level')
            .select('year, geofips, location_name')
            .ilike('location_name', `%${currentLocation}%`)
            .order('year', { ascending: false })
            .limit(1);
          
          if (nameResult.data && nameResult.data.length > 0) {
            years = nameResult.data;
            yearsError = nameResult.error;
          }
        }

        if (yearsError) {
          return;
        }

        const latestYear = years && years.length > 0 ? Number(years[0].year) : null;
        if (!latestYear) {
          return;
        }

        // Use the geofips we found (might be from location_name lookup)
        const actualGeofips = years && years[0].geofips ? Number(years[0].geofips) : geofips;

        // Fetch local data - use geofips (numeric) FIRST
        let { data: allLocalData, error: localError } = await supabaseClient
          .from('job_by_wage_level')
          .select('job_type, median_income, gender')
          .eq('geofips', actualGeofips)
          .eq('year', latestYear);

        // If no data by geofips, try geoid (text) as fallback
        if ((!allLocalData || allLocalData.length === 0) && !localError) {
          const geoid = String(actualGeofips);
          const geoidResult = await supabaseClient
            .from('job_by_wage_level')
            .select('job_type, median_income, gender')
            .eq('geoid', geoid)
            .eq('year', latestYear);
          
          if (geoidResult.data && geoidResult.data.length > 0) {
            allLocalData = geoidResult.data;
            localError = geoidResult.error;
          }
        }

        // Fetch California data - use geofips (numeric) FIRST
        let { data: allCaData, error: caError } = await supabaseClient
          .from('job_by_wage_level')
          .select('job_type, median_income, gender')
          .eq('geofips', 6) // California geofips
          .eq('year', latestYear);

        // If no CA data by geofips, try geoid (text) as fallback
        if ((!allCaData || allCaData.length === 0) && !caError) {
          const caGeoidResult = await supabaseClient
            .from('job_by_wage_level')
            .select('job_type, median_income, gender')
            .eq('geoid', '6') // California geoid (text)
            .eq('year', latestYear);
          
          if (caGeoidResult.data && caGeoidResult.data.length > 0) {
            allCaData = caGeoidResult.data;
            caError = caGeoidResult.error;
          }
        }

        if (localError || caError) {
          return;
        }

        if (!allLocalData || allLocalData.length === 0) {
          return;
        }

        // Filter to main sectors and gender='both' (or any if 'both' doesn't exist)
        const hasBothGenderLocal = allLocalData.some((r: any) => (r.gender || '').toLowerCase() === 'both');
        const hasBothGenderCA = (allCaData || []).some((r: any) => (r.gender || '').toLowerCase() === 'both');

        const localData = allLocalData.filter((r: any) => {
          const jobType = (r.job_type || '').trim();
          const gender = (r.gender || '').trim();
          // Use some() to handle potential whitespace differences
          const isMainSector = MAIN_SECTORS.some(sector => sector.trim() === jobType);
          // If 'both' exists in data, only use 'both'. Otherwise, use any gender.
          const genderMatch = hasBothGenderLocal ? gender.toLowerCase() === 'both' : true;
          return isMainSector && genderMatch;
        });

        const caData = (allCaData || []).filter((r: any) => {
          const jobType = (r.job_type || '').trim();
          const gender = (r.gender || '').trim();
          // Use some() to handle potential whitespace differences
          const isMainSector = MAIN_SECTORS.some(sector => sector.trim() === jobType);
          // If 'both' exists in data, only use 'both'. Otherwise, use any gender.
          const genderMatch = hasBothGenderCA ? gender.toLowerCase() === 'both' : true;
          return isMainSector && genderMatch;
        });

        if (!localData || localData.length === 0) {
          return;
        }

        // Calculate metrics (simple, inline)
        const wages = localData.map((r: any) => Number(r.median_income || 0)).filter((v: number) => v > 0);
        const highestWage = wages.length > 0 ? Math.max(...wages) : null;
        const lowestWage = wages.length > 0 ? Math.min(...wages) : null;
        const wageGapRatio = highestWage && lowestWage ? highestWage / lowestWage : null;

        // Compare to CA
        const caMap = new Map(caData?.map((d: any) => [d.job_type, Number(d.median_income || 0)]) || []);
        let sectorsAbove = 0;
        localData.forEach((local: any) => {
          const caWage = caMap.get(local.job_type) || 0;
          if (Number(local.median_income || 0) > caWage) sectorsAbove++;
        });

        setJobWagesMetrics({
          highestWage,
          lowestWage,
          wageGapRatio,
          sectorsAboveCA: sectorsAbove,
          latestYear
        });
      } catch (err) {
        console.error('Error loading job wages metrics:', err);
      }
    };

    loadJobWagesMetrics();
  }, [currentLocation]);

  const toggleEducationalView = (view: string) => {
    setActiveEducationalView(view);
  };

  const handlePeopleMapDataLoaded = useCallback((metrics: {
    currentValue: number;
    californiaAverage: number;
    year: number;
    variable: string;
  }) => {
    setPeopleMapMetrics(metrics);
  }, []);



  if (!currentLocation) {
    return (
      <div>
        <PageHeader 
          title="People & Demographics" 
          subtitle="Please select a location to view demographic data."
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div>
        <PageHeader 
          title="Loading..." 
          subtitle="Please wait while we load the data."
        />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageHeader 
          title="Error" 
          subtitle={error}
        />
      </div>
    );
  }

  return (
    <div data-report-root="people">
      <PageHeader 
        title={`People & Demographics - ${currentLocation}`}
        subtitle="Comprehensive demographic insights and trends for the selected location."
        variant="people"
      />

      {/* Population Projection Section */}
      {/* Chart 1: Housing to Jobs Balance */}
      <SectionWrapper sectionId="housing_to_employees" bgMap={PEOPLE_SECTION_BG} refs={sectionRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
        <HousingToEmployees
          location={currentLocation}
          geofips={selectedGeofips || null}
          preferredState="California"
        />
      </SectionWrapper>

      {/* Selected Demographic Characteristics (Workers by Sex, Householders by Race, Race Pie Chart) */}
      <SectionWrapper sectionId="population_trends" bgMap={PEOPLE_SECTION_BG} refs={sectionRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
        <PopulationTrendsSection
          location={currentLocation}
          geofips={selectedGeofips ? String(selectedGeofips) : null}
        />
      </SectionWrapper>

      {/* Employment Section */}
      <SectionWrapper sectionId="employment" bgMap={PEOPLE_SECTION_BG} refs={sectionRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
        <EmploymentSection
          location={currentLocation}
          geofips={selectedGeofips ? String(selectedGeofips) : null}
        />
      </SectionWrapper>

      {/* Housing Burden Section */}
      <SectionWrapper sectionId="housing_burden" bgMap={PEOPLE_SECTION_BG} refs={sectionRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
        <HousingBurdenSection
          location={currentLocation}
          geofips={selectedGeofips ? String(selectedGeofips) : null}
        />
      </SectionWrapper>

      {/* Cash Public Assistance & SNAP */}
      {isSectionEnabled('public_assistance') && currentLocation && (
        <SectionWrapper sectionId="public_assistance" bgMap={PEOPLE_SECTION_BG} refs={sectionRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
          <CashPublicAssistanceChart location={currentLocation} />
        </SectionWrapper>
      )}

      {/* Neighborhood Opportunity Score */}
      {isSectionEnabled('neighborhood_opportunity') && currentLocation && (
        <SectionWrapper sectionId="neighborhood_opportunity" bgMap={PEOPLE_SECTION_BG} refs={sectionRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
          <NeighborhoodOpportunityChart location={currentLocation} />
        </SectionWrapper>
      )}
      {/* Homelessness (county PIT totals + demographic breakdowns) */}
      <SectionWrapper sectionId="homelessness" bgMap={PEOPLE_SECTION_BG} refs={sectionRefs} visible={sectionVisible} getSectionStyle={(id: string) => ({
        ...getSectionStyle(id),
        display: homelessnessSectionEnabled ? 'block' : 'none',
      })}>
        <HomelessnessSection location={currentLocation} />
      </SectionWrapper>

      {/* Public Safety Section — CA DOJ violent crime rate */}
      {crimeSafetySectionEnabled && (
        <div style={{ order: getSectionOrder('crime_safety') }}>
          <CrimeSafetySection location={currentLocation} />
        </div>
      )}

      {/* Health Indicators Section (includes Health Insurance + CalEnviroScreen indicators) */}
      <SectionWrapper sectionId="health_indicators" bgMap={PEOPLE_SECTION_BG} refs={sectionRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
      <ChartLayout
        scrollAnchorId="chart-health"
        title="Health Indicators"
        chartType="bar"
        keyMetrics={
          activeHealthChart === 'health-insurance' ? (
            <KeyMetrics>
              <KeyMetric
                value={rowData?.indicator1 || '—'}
                label="Residents with no health insurance (latest year)"
                color="#1e3a8a"
              />
              <KeyMetric
                value={rowData?.indicator2 || '—'}
                label="Change in percentage points since 2012"
                color="#dc2626"
              />
            </KeyMetrics>
          ) : activeHealthChart === 'asthma' && (healthIndicatorMetrics.asthma.location != null || healthIndicatorMetrics.asthma.ca != null) ? (
            <HealthIndicatorKeyMetrics metrics={healthIndicatorMetrics.asthma} locationLabel={`${currentLocation} (asthma ED rate)`} />
          ) : activeHealthChart === 'cardiovascular' && (healthIndicatorMetrics.cardiovascular_disease.location != null || healthIndicatorMetrics.cardiovascular_disease.ca != null) ? (
            <HealthIndicatorKeyMetrics metrics={healthIndicatorMetrics.cardiovascular_disease} locationLabel={`${currentLocation} (CVD rate)`} />
          ) : activeHealthChart === 'lowBirthWeight' && (healthIndicatorMetrics.low_birth_weight.location != null || healthIndicatorMetrics.low_birth_weight.ca != null) ? (
            <HealthIndicatorKeyMetrics metrics={healthIndicatorMetrics.low_birth_weight} locationLabel={`${currentLocation} (low birth weight)`} />
          ) : undefined
        }
        chartSelector={{
          options: allowedHealthOptions.map((option) => ({ value: option.value, label: option.label })),
          value: activeHealthChart,
          onChange: (value) => setActiveHealthChart(value as 'asthma' | 'cardiovascular' | 'lowBirthWeight' | 'health-insurance')
        }}
        chartContent={
          activeHealthChart === 'health-insurance' ? (
            <div style={{ minHeight: '400px', display: 'flex', alignItems: 'flex-start' }}>
              {chartData ? (
                <ChartRendererSupabase
                  chartData={chartData.chartData}
                  chartOptions={chartData.chartOptions}
                  height="320px"
                />
              ) : (
                <div className="placeholder-chart">
                  <p>Loading health insurance data...</p>
                </div>
              )}
            </div>
          ) : activeHealthChart === 'asthma' ? (
            <HealthAsthmaChart location={currentLocation} />
          ) : activeHealthChart === 'cardiovascular' ? (
            <HealthCardiovascularChart location={currentLocation} />
          ) : (
            <HealthLowBirthWeightChart location={currentLocation} />
          )
        }
        overviewContent={
          activeHealthChart === 'health-insurance' ? (
            <OverviewContent
              title="Health Insurance Summary"
              description="This chart displays health insurance coverage rates for the selected location, showing trends over time and comparisons with state and county data."
            >
              <p>
                In {currentLocation}, {rowData?.indicator1 ?? '—'} of residents had no health insurance in the latest year, with a change of {rowData?.indicator2 ?? '—'} percentage points since 2012.
                Across many counties and places, the percentage of people with health insurance began significantly increasing between 2013 and 2018. Health insurance coverage rates are a valuable indicator for local governments aligning climate action with public health and resilience goals.
              </p>
            </OverviewContent>
          ) : activeHealthChart === 'asthma' ? (
            <HealthIndicatorOverview
              metrics={healthIndicatorMetrics.asthma}
              location={currentLocation}
              indicatorName="asthma emergency department visit rate"
              locationValueSuffix=" (age-adjusted)"
              explanation="Asthma rates reflect the prevalence of respiratory conditions that can be exacerbated by air pollution and climate-related factors like wildfire smoke. Higher asthma rates indicate greater vulnerability to poor air quality and climate impacts."
            />
          ) : activeHealthChart === 'cardiovascular' ? (
            <HealthIndicatorOverview
              metrics={healthIndicatorMetrics.cardiovascular_disease}
              location={currentLocation}
              indicatorName="cardiovascular disease rate"
              explanation="Cardiovascular disease rates indicate the prevalence of heart and circulatory system conditions. These conditions can be worsened by air pollution, extreme heat, and other climate-related stressors."
            />
          ) : (
            <HealthIndicatorOverview
              metrics={healthIndicatorMetrics.low_birth_weight}
              location={currentLocation}
              indicatorName="low birth weight rate"
              valueSuffix="%"
              explanation="Low birth weight rates reflect the percentage of babies born weighing less than 2,500 grams. This indicator is associated with environmental exposures and can be influenced by air quality, heat stress, and other environmental factors."
            />
          )
        }
        relevanceContent={
          activeHealthChart === 'health-insurance' ? (
            <RelevanceContent
              title="Relevance to Climate Action"
              description="Health insurance coverage rates are a valuable indicator for local governments aligning climate action with public health and resilience goals."
            >
              <div className="methodology-details-simple">
                <h5>Climate-Health Integration</h5>
                <p>As climate impacts like extreme heat, wildfire smoke, and respiratory illness intensify, access to healthcare becomes essential for adaptation and resilience.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Policy Framework</h5>
                <p>This supports integration of health equity into climate action planning under statewide mandates like SB 1000 and AB 32's environmental justice provisions.</p>
              </div>
            </RelevanceContent>
          ) : (
            <RelevanceContent
              title="Relevance to Climate Action"
              description="Health indicators help identify communities most vulnerable to climate impacts and prioritize climate action that protects public health."
            >
              <div className="methodology-details-simple">
                <h5>Vulnerability Assessment</h5>
                <p>Health indicators help identify populations most at risk from climate-related health impacts, enabling targeted interventions and resource allocation.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Climate-Health Integration</h5>
                <p>Understanding health burdens supports integration of health equity into climate action planning, ensuring climate policies protect and improve public health.</p>
              </div>
            </RelevanceContent>
          )
        }
        equityContent={
          activeHealthChart === 'health-insurance' ? (
            <EquityContent
              title="Equity Considerations"
              description="Health insurance coverage varies significantly by race and income, shaping who can access care during climate-related emergencies."
            >
              <div className="methodology-details-simple">
                <h5>Vulnerability Assessment</h5>
                <p>Communities of color remain more likely to be uninsured or underinsured, leaving them vulnerable to worsening air quality, heat-related illness, and chronic disease exacerbated by climate change.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Action Planning</h5>
                <p>By examining insurance coverage alongside race and income, local governments can identify underserved populations and prioritize climate-health interventions that ensure all residents benefit from climate resilience strategies.</p>
              </div>
            </EquityContent>
          ) : (
            <EquityContent
              title="Equity Considerations"
              description="Health disparities reflect historical and ongoing environmental injustices that must be addressed through equitable climate action."
            >
              <div className="methodology-details-simple">
                <h5>Environmental Justice</h5>
                <p>Communities with higher health burdens often face greater exposure to pollution and climate risks. Equitable climate action must prioritize these communities for protection and benefits.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Health Equity</h5>
                <p>Climate policies should reduce health disparities by improving air quality, reducing heat exposure, and ensuring all communities have access to climate resilience resources.</p>
              </div>
            </EquityContent>
          )
        }
        methodologyContent={
          activeHealthChart === 'health-insurance' ? (
            <MethodologyContent
              title="Methodology"
              description="Health insurance data comes from the American Community Survey, providing detailed statistics on coverage rates across different demographic groups."
              dataSources={[
                'American Community Survey (ACS) 5-year estimates',
                'Table B27001: Health Insurance Coverage by Sex by Age',
                'Individuals without health insurance coverage',
                'Disaggregated by sex and age group'
              ]}
              methodology={[
                'Data represents percentage of population without health insurance coverage',
                'Calculated from ACS 5-year estimates for geographic areas',
                'Includes comparisons with state and county data when available',
                'Trends analyzed over multiple time periods'
              ]}
            />
          ) : (
            <MethodologyContent
              title="Methodology"
              description="Health indicators are sourced from CalEnviroScreen 4.0, California's environmental health screening tool."
              dataSources={[
                'CalEnviroScreen 4.0 Results',
                'California Office of Environmental Health Hazard Assessment (OEHHA)',
                'Aggregated by place, county, and state',
                'Data represents environmental health indicators'
              ]}
              methodology={[
                'Data from CalEnviroScreen 4.0 aggregated to city, county, and state levels',
                'Values represent health indicator rates for the selected location',
                'California state average calculated from state-level aggregation',
                'Comparison shows location relative to statewide average',
                'Higher values indicate greater health burden'
              ]}
            />
          )
        }
        activeView={activeHealthView}
        onViewChange={(view: string) => setActiveHealthView(view)}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        appendLocationToTitle={true}
        overviewSectionId="people_equity"
        overviewChartId="income_by_race"
        onDownloadPNG={() => {
          if (activeHealthChart === 'health-insurance') {
            downloadCanvasAsPng('#health-insurance-chart', `Health_Insurance_Coverage_${currentLocation.replace(/\s+/g, '_')}`);
          } else {
            downloadCanvasAsPng('.chart-container', `Health_Indicators_${currentLocation.replace(/\s+/g, '_')}`);
          }
        }}
        onDownloadCSV={() => {}}
        pngFilename={`Health_Indicators_${currentLocation.replace(/\s+/g, '_')}`}
        csvFilename={`Health_Indicators_Data_${currentLocation.replace(/\s+/g, '_')}`}
      />
      </SectionWrapper>

      {/* Educational Attainment Section */}
      <SectionWrapper sectionId="educational_attainment" bgMap={PEOPLE_SECTION_BG} refs={sectionRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
      <ChartLayout
        scrollAnchorId="chart-educational-attainment-by-race"
        title="Educational Attainment"
        chartType="bar"
        keyMetrics={
          educationalAttainmentView === 'map' && educationalAttainmentMetrics ? (
            <KeyMetrics>
              <KeyMetric
                value={`${educationalAttainmentMetrics.currentValue.toFixed(1)}%`}
                label="Bachelor's Degree or Higher"
              />
              <KeyMetric
                value={`${educationalAttainmentMetrics.californiaAverage.toFixed(1)}%`}
                label="California Average"
              />
            </KeyMetrics>
          ) : educationalAttainmentView === 'trends' && educationalTrendsData?.chartData?.datasets?.length ? (
            (() => {
              const locDs = educationalTrendsData.chartData.datasets.find((d: { label: string }) =>
                d.label.toLowerCase() !== 'california'
              );
              const caDs = educationalTrendsData.chartData.datasets.find((d: { label: string }) =>
                d.label.toLowerCase() === 'california'
              );
              const locLatest = locDs?.data?.length ? locDs.data[locDs.data.length - 1] : null;
              const caLatest = caDs?.data?.length ? caDs.data[caDs.data.length - 1] : null;
              const locNum = locLatest != null ? Number(locLatest) : null;
              const caNum = caLatest != null ? Number(caLatest) : null;
              const vsCa = locNum != null && caNum != null ? (locNum > caNum ? 'Above' : 'Below') : null;
              const vsCaColor = vsCa === 'Above' ? '#10b981' : '#dc2626';
              return (
                <KeyMetrics>
                  {locLatest != null && (
                    <KeyMetric value={`${Number(locLatest).toFixed(1)}%`} label={`${currentLocation} (latest year)`} color="#1e3a8a" />
                  )}
                  {caLatest != null && (
                    <KeyMetric value={`${Number(caLatest).toFixed(1)}%`} label="California average" color="#059669" />
                  )}
                  {vsCa != null && (
                    <KeyMetric value={vsCa} label="vs. California" color={vsCaColor} />
                  )}
                </KeyMetrics>
              );
            })()
          ) : educationalAttainmentView === 'chart' && educationalChartData?.chartData?.datasets?.[0]?.data ? (
            (() => {
              const locPcts = (educationalChartData.chartData.datasets[0].data as number[]).filter((v: number) => typeof v === 'number' && v > 0);
              const highest = locPcts.length ? Math.max(...locPcts) : null;
              const lowest = locPcts.length ? Math.min(...locPcts) : null;
              const ratio = highest != null && lowest != null && lowest > 0 ? highest / lowest : null;
              return (
                <KeyMetrics>
                  {highest != null && (
                    <KeyMetric value={`${highest.toFixed(1)}%`} label="Highest (by race)" color="#1e3a8a" />
                  )}
                  {lowest != null && (
                    <KeyMetric value={`${lowest.toFixed(1)}%`} label="Lowest (by race)" color="#dc2626" />
                  )}
                  {ratio != null && (
                    <KeyMetric value={`${ratio.toFixed(1)}x`} label="Highest/Lowest ratio" color="#059669" />
                  )}
                </KeyMetrics>
              );
            })()
          ) : (
            <KeyMetrics>
              <KeyMetric 
                value="—" 
                label="Highest (by race)"
                color="#1e3a8a"
              />
              <KeyMetric 
                value="—" 
                label="Lowest (by race)"
                color="#dc2626"
              />
              <KeyMetric 
                value="—" 
                label="Highest/Lowest ratio"
                color="#059669"
              />
            </KeyMetrics>
          )
        }
        chartContent={
          educationalAttainmentView === 'map' ? (
            <GenericMapTiered
              location={currentLocation}
              lockedVariable="educational_attainment"
              hideVariableSelector={true}
              onDataLoaded={(metrics) => {
                setEducationalAttainmentMetrics(metrics);
              }}
            />
          ) : educationalAttainmentView === 'trends' ? (
            educationalTrendsData ? (
              <ChartRendererSupabase
                chartData={educationalTrendsData.chartData}
                chartOptions={educationalTrendsData.chartOptions}
                height="360px"
              />
            ) : (
              <div className="placeholder-chart">
                <p>Loading educational attainment over time...</p>
              </div>
            )
          ) : educationalChartData ? (
            <div style={{ minHeight: '400px', display: 'flex', alignItems: 'flex-start' }}>
              <ChartRendererSupabase
                chartData={educationalChartData.chartData}
                chartOptions={educationalChartData.chartOptions}
                height="360px"
              />
            </div>
          ) : (
            <div className="placeholder-chart">
              <p>Loading educational attainment data...</p>
            </div>
          )
        }
        chartSelector={{
          options: allowedEducationOptions.map((option) => ({ value: option.value, label: option.label })),
          value: educationalAttainmentView,
          onChange: (value: string) => setEducationalAttainmentView(value as 'chart' | 'trends' | 'map')
        }}
        overviewContent={
          educationalAttainmentView === 'map' && educationalAttainmentMetrics ? (
            <OverviewContent
              title="Bachelor's Degree or Higher Overview"
              description="This map visualizes the percentage of adults 25+ with bachelor's degree or higher across census tracts."
            >
              <p>
                {currentLocation} has an educational attainment rate of {educationalAttainmentMetrics.currentValue.toFixed(1)}% (adults 25+ with bachelor's degree or higher), compared to the California average of {educationalAttainmentMetrics.californiaAverage.toFixed(1)}%—{educationalAttainmentMetrics.currentValue > educationalAttainmentMetrics.californiaAverage ? 'higher than' : 'lower than'} the state average. Data is from {educationalAttainmentMetrics.year}. Educational attainment is a key indicator of community capacity for understanding and participating in climate action initiatives.
              </p>
            </OverviewContent>
          ) : educationalAttainmentView === 'trends' && educationalTrendsData?.chartData?.datasets?.length ? (
            (() => {
              const locDs = educationalTrendsData.chartData.datasets.find((d: { label: string }) => d.label.toLowerCase() !== 'california');
              const caDs = educationalTrendsData.chartData.datasets.find((d: { label: string }) => d.label.toLowerCase() === 'california');
              const locLatest = locDs?.data?.length ? locDs.data[locDs.data.length - 1] : null;
              const caLatest = caDs?.data?.length ? caDs.data[caDs.data.length - 1] : null;
              const years = educationalTrendsData.chartData.labels || [];
              const range = years.length ? `${years[0]}–${years[years.length - 1]}` : '';
              return (
                <OverviewContent
                  title="Educational Attainment Over Time"
                  description="Trend in the percentage of adults 25+ with bachelor's degree or higher, regardless of race, for the location and California."
                >
                  <p>
                    {locLatest != null && caLatest != null ? (
                      <>From {range}, the share of adults with a bachelor's degree or higher in {currentLocation} reached {Number(locLatest).toFixed(1)}% in the latest year, compared to {Number(caLatest).toFixed(1)}% for California—{Number(locLatest) > Number(caLatest) ? 'higher than' : 'lower than'} the state average. </>
                    ) : locLatest != null ? (
                      <>Over time, {currentLocation}'s bachelor's degree attainment (adults 25+) reached {Number(locLatest).toFixed(1)}% in the latest year. </>
                    ) : null}
                    This trend shows how overall educational attainment has changed and helps inform climate outreach and workforce development strategies.
                  </p>
                </OverviewContent>
              );
            })()
          ) : (
            <OverviewContent
              title="Educational Summary (By Race)"
              description="This chart shows bachelor's degree attainment by race and ethnicity for the most recent year—different views in this section show the same underlying data by race, over time, or on a map."
            >
              <p>The chart displays the percentage of adults 25+ with a bachelor's degree or higher for each racial and ethnic group in {currentLocation} compared to California. Significant disparities between groups highlight the need for targeted educational equity initiatives. Educational attainment is a key indicator of community capacity for climate literacy and participation in climate action.</p>
            </OverviewContent>
          )
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Educational attainment is crucial for climate action planning as it influences community capacity for understanding, implementing, and sustaining climate initiatives."
          >
            <div className="methodology-details-simple">
              <h5>Climate Literacy</h5>
              <p>Higher education levels correlate with increased climate awareness and engagement, making this data essential for designing effective climate education and outreach programs.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Workforce Development</h5>
              <p>Educational attainment patterns inform strategies to build climate action workforce development programs that create pathways for all residents to participate in the green economy.</p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Educational disparities by race and ethnicity can create barriers to climate action participation and understanding, requiring targeted interventions."
          >
            <div className="methodology-details-simple">
              <h5>Access Barriers</h5>
              <p>Communities with lower educational attainment may face challenges accessing climate information, participating in planning processes, or benefiting from climate action programs.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Inclusive Planning</h5>
              <p>Understanding educational patterns helps ensure climate action planning processes are accessible and relevant to residents across all education levels and demographic groups.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="Educational attainment data comes from the American Community Survey, providing detailed statistics on education levels achieved by adults 25 years and older."
            dataSources={[
              'American Community Survey (ACS) 5-year estimates',
              'Adults 25 years and older',
              'Education level categories',
              'Geographic comparisons'
            ]}
            methodology={[
              'Data represents percentage of population with bachelor\'s degree or higher',
              'Calculated from ACS 5-year estimates for geographic areas',
              'Includes comparisons with state and county data when available',
              'Disaggregated by race and ethnicity for equity analysis'
            ]}
          />
        }
        activeView={activeEducationalView}
        onViewChange={toggleEducationalView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        appendLocationToTitle={true}
        className="alternating-bg"
        onDownloadPNG={() => {
          if (educationalAttainmentView === 'map') {
            return;
          }
          downloadCanvasAsPng('#educational-attainment-chart', `Educational_Attainment_${currentLocation.replace(/\s+/g, '_')}`);
        }}
        onDownloadCSV={() => {
          if (educationalAttainmentView === 'map') {
            return;
          }
        }}
        pngFilename={`Educational_Attainment_${currentLocation.replace(/\s+/g, '_')}`}
        csvFilename={`Educational_Attainment_Data_${currentLocation.replace(/\s+/g, '_')}`}
      />
      </SectionWrapper>

      {/* Income by Race Section */}
      <SectionWrapper sectionId="income_poverty" bgMap={PEOPLE_SECTION_BG} refs={sectionRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
      <ChartLayout
        scrollAnchorId="chart-income-poverty-by-race"
        title="Income and Poverty by Race"
        chartType="line"
        activeView={activeIncomeView}
        onViewChange={setActiveIncomeView}
        chartSelector={{
          options: allowedIncomeOptions.map((option) => ({ value: option.value, label: option.label })),
          value: activeIncomeRaceChart,
          onChange: setActiveIncomeRaceChart
        }}
        keyMetrics={
          activeIncomeRaceChart === 'income-trends' || activeIncomeRaceChart === 'income-bar' ? (
            <KeyMetrics>
              <KeyMetric
                value={incomePovertyMetrics.incomeRatio ? incomePovertyMetrics.incomeRatio.toFixed(1) + 'x' : '—'}
                label="Highest/Lowest Income Ratio"
                color="#1e3a8a"
              />
              <KeyMetric
                value={incomePovertyMetrics.lowestIncome ? `$${Math.round(incomePovertyMetrics.lowestIncome).toLocaleString()}` : '—'}
                label="Lowest Median Income"
                color="#dc2626"
              />
            </KeyMetrics>
          ) : (
            <KeyMetrics>
              <KeyMetric
                value={incomePovertyMetrics.povertyRatio ? incomePovertyMetrics.povertyRatio.toFixed(1) + 'x' : '—'}
                label="Highest/Lowest Poverty Ratio"
                color="#1e3a8a"
              />
              <KeyMetric
                value={incomePovertyMetrics.highestPovertyRate ? `${incomePovertyMetrics.highestPovertyRate.toFixed(1)}%` : '—'}
                label="Highest Poverty Rate"
                color="#dc2626"
              />
            </KeyMetrics>
          )
        }
        chartContent={
          activeIncomeRaceChart === 'income-trends' ? (
            <div id="income-by-race-trends-chart">
              <IncomeByRaceTrends key={currentLocation} location={currentLocation} />
            </div>
          ) : activeIncomeRaceChart === 'poverty-trends' ? (
            <div id="poverty-by-race-trends-chart">
              <PovertyByRaceTrends location={currentLocation} />
            </div>
          ) : activeIncomeRaceChart === 'income-bar' ? (
            <div id="income-by-race-bar-chart">
              <IncomeByRaceBar key={currentLocation} location={currentLocation} />
            </div>
          ) : (
            <div id="poverty-by-race-bar-chart">
              <PovertyByRaceBar location={currentLocation} />
            </div>
          )
        }
        onDownloadPNG={() => {
          const chartId = activeIncomeRaceChart === 'income-trends' ? '#income-by-race-trends-chart' :
                          activeIncomeRaceChart === 'poverty-trends' ? '#poverty-by-race-trends-chart' :
                          activeIncomeRaceChart === 'income-bar' ? '#income-by-race-bar-chart' :
                          '#poverty-by-race-bar-chart';
          const filename = activeIncomeRaceChart === 'income-trends' ? 'Income_by_Race_Trends' :
                          activeIncomeRaceChart === 'poverty-trends' ? 'Poverty_by_Race_Trends' :
                          activeIncomeRaceChart === 'income-bar' ? 'Income_by_Race_Bar' :
                          'Poverty_by_Race_Bar';
          downloadCanvasAsPng(chartId, `${currentLocation.replace(/\s+/g, '_')}_${filename}`);
        }}
        onDownloadCSV={() => {}}
        pngFilename={`${currentLocation.replace(/\s+/g, '_')}_Income_Poverty_by_Race`}
        csvFilename={`${currentLocation.replace(/\s+/g, '_')}_Income_Poverty_Data`}
        overviewContent={
          activeIncomeRaceChart === 'income-trends' ? (
            <OverviewContent
              title="Income Trends Overview"
              description="Median household income trends by race and ethnicity over time, showing whether income gaps between racial groups are widening or narrowing."
            >
              <p>
                {incomePovertyMetrics.incomeRatio != null && incomePovertyMetrics.lowestIncome != null ? (
                  <>In {currentLocation}, the ratio of highest to lowest median income across racial groups is {incomePovertyMetrics.incomeRatio.toFixed(1)}x, with the lowest median income among racial groups at ${Math.round(incomePovertyMetrics.lowestIncome).toLocaleString()}. </>
                ) : null}
                These disparities shape residents' ability to participate in and benefit from climate policies and clean technology adoption. Tracking trends over time reveals whether gaps are narrowing or widening and helps target equitable climate programs.
              </p>
            </OverviewContent>
          ) : activeIncomeRaceChart === 'poverty-trends' ? (
            <OverviewContent
              title="Poverty Trends Overview"
              description="Poverty rates by race and ethnicity over time, revealing structural inequalities that impact climate vulnerability and resilience."
            >
              <p>
                {incomePovertyMetrics.povertyRatio != null && incomePovertyMetrics.highestPovertyRate != null ? (
                  <>In {currentLocation}, the ratio of highest to lowest poverty rate across racial groups is {incomePovertyMetrics.povertyRatio.toFixed(1)}x, with the highest poverty rate at {incomePovertyMetrics.highestPovertyRate.toFixed(1)}%. </>
                ) : null}
                Communities with higher poverty rates face greater barriers to climate solutions and increased vulnerability to climate impacts. Tracking these trends helps prioritize income-qualified programs and equitable climate action.
              </p>
            </OverviewContent>
          ) : activeIncomeRaceChart === 'income-bar' ? (
            <OverviewContent
              title="Income by Race Overview"
              description="Median household income by race and ethnicity for the most recent year—a snapshot of current economic disparities."
            >
              <p>
                {incomePovertyMetrics.incomeRatio != null && incomePovertyMetrics.lowestIncome != null ? (
                  <>In {currentLocation}, the highest-earning racial group has median income {incomePovertyMetrics.incomeRatio.toFixed(1)}x that of the lowest-earning group; the lowest median income is ${Math.round(incomePovertyMetrics.lowestIncome).toLocaleString()}. </>
                ) : null}
                These income disparities directly shape who can invest in clean technologies and participate in climate programs. Equitable climate action requires targeting support to communities with lower median incomes.
              </p>
            </OverviewContent>
          ) : (
            <OverviewContent
              title="Poverty by Race Overview"
              description="Poverty rates by race and ethnicity for the most recent year—showing which groups face the highest economic barriers."
            >
              <p>
                {incomePovertyMetrics.povertyRatio != null && incomePovertyMetrics.highestPovertyRate != null ? (
                  <>In {currentLocation}, poverty rates vary widely by race: the ratio of highest to lowest poverty rate is {incomePovertyMetrics.povertyRatio.toFixed(1)}x, and the highest poverty rate among racial groups is {incomePovertyMetrics.highestPovertyRate.toFixed(1)}%. </>
                ) : null}
                Higher poverty rates indicate greater barriers to climate solutions and greater vulnerability to climate impacts. Climate programs should prioritize communities and racial groups with the highest poverty rates for targeted assistance.
              </p>
            </OverviewContent>
          )
        }
        relevanceContent={
          activeIncomeRaceChart === 'income-trends' ? (
          <RelevanceContent
            title="Relevance to Climate Action"
              description="Income disparities by race shape residents' ability to participate in and benefit from climate policies and clean technology adoption."
          >
            <div className="methodology-details-simple">
                <h5>Technology Access</h5>
                <p>Income directly influences access to clean technologies like electric vehicles, home energy retrofits, and rooftop solar panels. Lower-income households face barriers to these solutions without targeted support.</p>
            </div>
            <div className="methodology-details-simple">
                <h5>Climate Resilience</h5>
                <p>Households with higher incomes can better afford climate adaptation measures such as home weatherization, air conditioning, and disaster preparedness, while lower-income communities face greater exposure to climate risks.</p>
            </div>
          </RelevanceContent>
          ) : activeIncomeRaceChart === 'poverty-trends' ? (
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Poverty rates by race reveal structural inequities that shape how different communities experience climate risks and access climate solutions."
          >
            <div className="methodology-details-simple">
              <h5>Climate Vulnerability</h5>
                <p>Low-income communities and communities of color face greater exposure to climate risks such as extreme heat, flooding, and air pollution while lacking resources for adaptation measures.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Resource Access</h5>
                <p>Poverty limits access to clean technologies, energy-efficient housing, and climate resilience measures. Without targeted assistance, low-income households cannot participate in climate programs.</p>
            </div>
          </RelevanceContent>
          ) : activeIncomeRaceChart === 'income-bar' ? (
            <RelevanceContent
              title="Relevance to Climate Action"
              description="Current income levels by race directly shape residents' ability to invest in climate solutions and participate in climate action programs."
          >
            <div className="methodology-details-simple">
                <h5>Investment Capacity</h5>
                <p>Higher-income households can more readily invest in energy-efficient appliances, electric vehicles, and home electrification, while lower-income households require financial assistance to access these technologies.</p>
            </div>
            <div className="methodology-details-simple">
                <h5>Program Participation</h5>
                <p>Income disparities affect participation in climate programs. Targeted incentives and income-qualified rebates are needed to ensure equitable access across all racial groups.</p>
            </div>
            </RelevanceContent>
          ) : (
          <RelevanceContent
            title="Relevance to Climate Action"
              description="Poverty rates by race directly impact communities' capacity to participate in climate solutions and their vulnerability to climate risks."
          >
            <div className="methodology-details-simple">
                <h5>Financial Barriers</h5>
                <p>High poverty rates indicate communities with limited financial resources to invest in clean technologies, energy efficiency upgrades, or climate adaptation measures without substantial assistance.</p>
            </div>
            <div className="methodology-details-simple">
                <h5>Climate Vulnerability</h5>
                <p>Communities with higher poverty rates are often more exposed to climate risks due to older housing stock, limited access to cooling and heating systems, and limited resources for disaster preparedness.</p>
            </div>
          </RelevanceContent>
          )
        }
        equityContent={
          activeIncomeRaceChart === 'income-trends' ? (
          <EquityContent
            title="Equity Considerations"
              description="Income gaps by race reflect historical and systemic inequities that climate policies must address to ensure equitable benefits and participation."
          >
            <div className="methodology-details-simple">
              <h5>Historical Context</h5>
                <p>Discriminatory practices in housing, employment, and lending have created persistent wealth gaps across racial groups that continue to limit access to climate solutions.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Policy Design</h5>
                <p>Climate policies that ignore income disparities risk reinforcing inequality by primarily benefiting higher-income households. Targeted programs with income-based assistance are essential for equity.</p>
            </div>
          </EquityContent>
          ) : activeIncomeRaceChart === 'poverty-trends' ? (
          <EquityContent
            title="Equity Considerations"
            description="Racial disparities in poverty reflect long-standing systemic injustices that require targeted interventions in climate action planning."
          >
            <div className="methodology-details-simple">
              <h5>Systemic Barriers</h5>
                <p>Historical discrimination in housing, employment, and lending has created persistent wealth gaps that limit climate resilience and compound vulnerability to climate impacts.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Targeted Solutions</h5>
                <p>Climate policies must address root causes of poverty while ensuring equitable access to climate benefits and resources through income-qualified programs and community-focused initiatives.</p>
            </div>
          </EquityContent>
          ) : activeIncomeRaceChart === 'income-bar' ? (
            <EquityContent
              title="Equity Considerations"
              description="Income disparities by race must be addressed through equitable program design that prioritizes communities with lower median incomes."
          >
            <div className="methodology-details-simple">
                <h5>Program Prioritization</h5>
                <p>Climate programs should prioritize communities with lower median incomes to ensure that all residents can benefit from clean energy and climate resilience measures, regardless of race.</p>
            </div>
            <div className="methodology-details-simple">
                <h5>Barrier Removal</h5>
                <p>Financial barriers to climate solutions disproportionately affect communities of color with lower incomes. Policies must include direct financial assistance and low-cost financing options.</p>
            </div>
            </EquityContent>
          ) : (
          <EquityContent
            title="Equity Considerations"
              description="Addressing poverty disparities by race is essential for achieving equitable climate outcomes and ensuring all communities can participate in and benefit from climate action."
          >
            <div className="methodology-details-simple">
                <h5>Priority Communities</h5>
                <p>Racial groups with higher poverty rates should be prioritized for climate programs, financial assistance, and community-based climate resilience initiatives to address historical inequities.</p>
            </div>
            <div className="methodology-details-simple">
                <h5>Comprehensive Approach</h5>
                <p>Climate policies must go beyond technology deployment to address underlying economic disparities, ensuring that climate action contributes to poverty reduction and economic opportunity for all racial groups.</p>
            </div>
          </EquityContent>
          )
        }
        methodologyContent={
          activeIncomeRaceChart === 'income-trends' ? (
          <MethodologyContent
            title="Methodology"
              description="Data are from the US Census American Community Survey 5-year estimates (ACS5) via the median_income_by_race table. Median household incomes are calculated and reported by race and ethnicity for the selected location."
            dataSources={[
                'US Census American Community Survey 5-year estimates (ACS5)',
                'Database table: median_income_by_race',
                'Race/ethnicity classifications follow US Census definitions'
            ]}
            methodology={[
                'Resolve geofips for selected location',
                'Query median_income_by_race table by geoid',
                'Group data by race_or_ethnicity and year',
                'Plot median_income values as time series lines by race'
              ]}
            />
          ) : activeIncomeRaceChart === 'poverty-trends' ? (
            <MethodologyContent
              title="Methodology"
              description="Data are sourced from the US Census American Community Survey 5-year estimates (ACS5) via the poverty_by_race table. Poverty rates are calculated as the percentage of individuals in each racial group whose household income falls below the federal poverty threshold."
              dataSources={[
                'US Census American Community Survey 5-year estimates (ACS5)',
                'Database table: poverty_by_race',
                'Poverty thresholds vary by household size and location',
                'Race/ethnicity classifications follow US Census definitions'
              ]}
              methodology={[
                'Resolve geofips for selected location',
                'Query poverty_by_race table by geoid',
                'Group data by race and year',
                'Plot percentage values as time series lines by race'
              ]}
            />
          ) : activeIncomeRaceChart === 'income-bar' ? (
            <MethodologyContent
              title="Methodology"
              description="Data are from ACS5 via the median_income_by_race table. The chart displays median household income values for the most recent year available, grouped by race and ethnicity."
              dataSources={[
                'US Census American Community Survey 5-year estimates (ACS5)',
                'Database table: median_income_by_race',
                'Race/ethnicity classifications follow US Census definitions'
              ]}
              methodology={[
                'Resolve geofips for selected location',
                'Query median_income_by_race table by geoid',
                'Identify latest available year',
                'Retrieve median_income values for all races in that year',
                'Display as grouped bar chart, sorted by income value'
              ]}
            />
          ) : (
          <MethodologyContent
            title="Methodology"
              description="Data are from ACS5 via the poverty_by_race table. The chart displays poverty rates (percentage of individuals in poverty) for the most recent year available, grouped by race and ethnicity."
            dataSources={[
                'US Census American Community Survey 5-year estimates (ACS5)',
                'Database table: poverty_by_race',
                'Poverty thresholds vary by household size and location',
                'Race/ethnicity classifications follow US Census definitions'
            ]}
            methodology={[
                'Resolve geofips for selected location',
                'Query poverty_by_race table by geoid',
                'Identify latest available year',
                'Retrieve percentage values for all races in that year',
                'Display as grouped bar chart, sorted by poverty rate'
              ]}
            />
          )
        }
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        appendLocationToTitle={true}
      />
      </SectionWrapper>

      {/* Economic Sector Wages Section */}
      <SectionWrapper sectionId="job_wages" bgMap={PEOPLE_SECTION_BG} refs={sectionRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
      <ChartLayout
        scrollAnchorId="chart-job-wages"
        title="Economic Sector Wages"
        chartType="bar"
        activeView={activeJobWagesNarrative}
        onViewChange={(view: string) =>
          setActiveJobWagesNarrative(view as 'overview' | 'relevance' | 'equity' | 'methodology')
        }
        chartSelector={{
          options: allowedJobWagesOptions.map((option) => ({ value: option.value, label: option.label })),
          value: activeJobWagesView,
          onChange: setActiveJobWagesView
        }}
        keyMetrics={
          <KeyMetrics>
            <KeyMetric 
              value={jobWagesMetrics.highestWage ? `$${Math.round(jobWagesMetrics.highestWage).toLocaleString()}` : '—'} 
              label="Highest Wage Sector" 
              color="#10b981"
            />
            <KeyMetric 
              value={jobWagesMetrics.lowestWage ? `$${Math.round(jobWagesMetrics.lowestWage).toLocaleString()}` : '—'} 
              label="Lowest Wage Sector" 
              color="#f59e0b"
            />
            <KeyMetric 
              value={jobWagesMetrics.wageGapRatio ? jobWagesMetrics.wageGapRatio.toFixed(1) + 'x' : '—'} 
              label="Wage Gap Ratio" 
              color="#1e3a8a"
            />
            {jobWagesMetrics.sectorsAboveCA !== null && (
              <KeyMetric 
                value={`${jobWagesMetrics.sectorsAboveCA}/5`} 
                label="Sectors Above CA Average" 
                color="#3b82f6"
              />
            )}
          </KeyMetrics>
        }
        chartContent={
          activeJobWagesView === 'current' ? (
            <div id="job-wages-current-chart">
              <JobWagesBySector location={currentLocation} />
            </div>
          ) : activeJobWagesView === 'trends' ? (
            <div id="job-wages-trends-chart">
              <JobWagesBySectorTrends location={currentLocation} />
            </div>
          ) : activeJobWagesView === 'median-trend' ? (
            <div id="job-wages-median-trend-chart">
              <JobWageMedianTrendComparison location={currentLocation} />
            </div>
          ) : (
            <div id="job-wages-comparison-chart">
              <JobWagesBySectorComparison location={currentLocation} />
            </div>
          )
        }
        overviewContent={
          <OverviewContent
            title="Economic Sector Wages Overview"
            description="This chart displays median income by major job sector, revealing which industries provide good-paying jobs and which workers may be vulnerable to economic transitions, including climate-related changes."
          >
            {jobWagesMetrics.highestWage && jobWagesMetrics.lowestWage ? (
              <p>
                In {jobWagesMetrics.latestYear}, {currentLocation} shows significant variation in wages across economic sectors. 
                The highest-paying sector offers median wages of ${jobWagesMetrics.highestWage.toLocaleString()}, 
                while the lowest-paying sector has ${jobWagesMetrics.lowestWage.toLocaleString()}, 
                creating a wage gap ratio of {jobWagesMetrics.wageGapRatio?.toFixed(1)}x. 
                {jobWagesMetrics.sectorsAboveCA !== null && jobWagesMetrics.sectorsAboveCA > 0 && (
                  <> Compared to California's average, {currentLocation} exceeds state averages in {jobWagesMetrics.sectorsAboveCA} out of 5 sectors.</>
                )}
                Understanding the wage distribution across different economic sectors helps identify opportunities for green job development and workers who may need support during climate transitions. Sectors with lower wages often face higher energy cost burdens, while high-wage sectors indicate economic strengths that can be leveraged for climate action.
              </p>
            ) : (
              <p>
                Understanding the wage distribution across different economic sectors helps identify opportunities for green job development and workers who may need support during climate transitions. Sectors with lower wages often face higher energy cost burdens, while high-wage sectors indicate economic strengths that can be leveraged for climate action.
              </p>
            )}
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Economic sector wages inform just transition planning, green job development, and equitable climate policy design."
          >
            <div className="methodology-details-simple">
              <h5>Green Job Opportunities</h5>
              <p>The professional and technical sectors (which include renewable energy, energy efficiency, and sustainable design jobs) typically offer competitive wages. Identifying wage levels in climate-friendly sectors helps demonstrate that the transition to a clean energy economy can provide good-paying career opportunities.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Just Transition Planning</h5>
              <p>Workers in traditional energy-intensive sectors (construction, production, transportation) may need retraining and support as the economy shifts to clean energy. Understanding current wage levels helps design transition programs that maintain or improve workers' economic security.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Energy Burden Context</h5>
              <p>Lower-wage sectors (service, retail, food preparation) often face higher energy costs as a percentage of income. This makes energy efficiency programs, weatherization assistance, and clean energy incentives critical for economic equity alongside climate action.</p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Wage disparities across economic sectors have direct implications for climate justice and equitable policy design."
          >
            <div className="methodology-details-simple">
              <h5>Economic Vulnerability</h5>
              <p>Workers in lower-wage sectors face compounding challenges: higher energy cost burdens, limited ability to invest in electric vehicles or home solar, and greater vulnerability to climate impacts like extreme heat. Climate policies must ensure these workers benefit from clean energy transitions through good jobs and direct assistance, not just higher costs.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Program Design</h5>
              <p>Income-qualified programs for energy efficiency, solar installations, and EV rebates should be calibrated to sector wage levels to ensure accessibility for workers across all occupation types. Workforce development programs should target pathways from lower-wage to higher-wage green sectors.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Community Benefits</h5>
              <p>Climate investments should prioritize creating quality jobs accessible to workers currently in lower-wage sectors, with clear career pathways, benefits, and living wages that support families and communities.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Data Source & Methodology"
            description="Job wage data are from the U.S. Census Bureau American Community Survey 5-Year estimates, providing reliable median income information by occupation category. Data availability varies by location - county-level data is most comprehensive while some city-level data may be limited. Includes only full-time, year-round workers (50+ weeks and 35+ hours per week). Chart shows major occupation categories; detailed subsector breakdowns are available in the source data. Values are in nominal dollars for the survey year."
            dataSources={[
              'U.S. Census Bureau American Community Survey 5-Year Estimates (ACS5)',
              'Table S2411: Occupation by Median Earnings',
              'Database table: job_by_wage_level',
              'Occupation categories follow U.S. Census Standard Occupational Classification (SOC) system'
            ]}
            methodology={[
              'Resolve geofips for selected location (county or city)',
              'Query job_by_wage_level table filtered to latest available year',
              'Extract major occupation categories (top-level sectors only)',
              'Display median annual earnings for full-time, year-round workers',
              'Sort by wage level from highest to lowest',
              'Color-code by relative wage level within location'
            ]}
          />
        }
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        appendLocationToTitle={true}
        onDownloadPNG={() => {
          const chartId = activeJobWagesView === 'current' ? '#job-wages-current-chart' :
                          activeJobWagesView === 'trends' ? '#job-wages-trends-chart' :
                          activeJobWagesView === 'median-trend' ? '#job-wages-median-trend-chart' :
                          '#job-wages-comparison-chart';
          const filename = activeJobWagesView === 'current' ? 'Job_Wages_Current' :
                          activeJobWagesView === 'trends' ? 'Job_Wages_Trends' :
                          activeJobWagesView === 'median-trend' ? 'Job_Wages_Median_Trend' :
                          'Job_Wages_Comparison';
          downloadCanvasAsPng(chartId, `${currentLocation.replace(/\s+/g, '_')}_${filename}`);
        }}
        onDownloadCSV={() => {}}
        pngFilename={`${currentLocation.replace(/\s+/g, '_')}_Economic_Sector_Wages`}
        csvFilename={`${currentLocation.replace(/\s+/g, '_')}_Job_Wages_Data`}
      />
      </SectionWrapper>

      {/* People-Related Maps */}
      <SectionWrapper sectionId="people_maps" bgMap={PEOPLE_SECTION_BG} refs={sectionRefs} visible={sectionVisible} getSectionStyle={(id: string) => ({
        ...getSectionStyle(id),
        display: peopleMapsEnabled ? 'block' : 'none',
      })}>

      <ChartLayout
          title="People-Related Maps"
          description="Interactive maps for demographic indicators such as median household income, political leanings, and population density."
          chartType="map"
          layout="vertical"
          keyMetrics={
            peopleMapMetrics ? (
              <KeyMetrics>
                <KeyMetric
                  value={formatPeopleMapMetric(peopleMapMetrics.variable, peopleMapMetrics.currentValue)}
                  label={
                    peopleMapMetrics.variable === 'income'
                      ? 'Median Household Income'
                      : peopleMapMetrics.variable === 'political_lean'
                      ? 'Political Lean'
                      : peopleMapMetrics.variable === 'population_density'
                      ? 'Population Density'
                      : 'Value'
                  }
                />
                <KeyMetric
                  value={formatPeopleMapMetric(peopleMapMetrics.variable, peopleMapMetrics.californiaAverage)}
                  label="California Average"
                />
              </KeyMetrics>
            ) : undefined
          }
          chartContent={
            <GenericMapTiered
              location={currentLocation}
              onDataLoaded={handlePeopleMapDataLoaded}
            allowedVariables={allowedPeopleMapVariables}
              enableScopeSelector={true}
            />
          }
          overviewContent={
            peopleMapMetrics ? (
              <OverviewContent
                title={
                  peopleMapMetrics.variable === 'income'
                    ? 'Median Household Income Map Overview'
                    : peopleMapMetrics.variable === 'political_lean'
                    ? 'Political Lean Map Overview'
                    : 'Population Density Map Overview'
                }
                description={`This map visualizes ${peopleMapMetrics.variable === 'income' ? 'median household income' : peopleMapMetrics.variable === 'political_lean' ? 'political leanings' : 'population density'} across census tracts in ${currentLocation}, helping identify spatial variation in key demographic indicators.`}
              >
                {peopleMapMetrics.variable === 'income' ? (
                  <p>
                    {currentLocation} has a median household income of ${Math.round(peopleMapMetrics.currentValue).toLocaleString()}, compared to the California average of ${Math.round(peopleMapMetrics.californiaAverage).toLocaleString()}—{peopleMapMetrics.currentValue > peopleMapMetrics.californiaAverage ? 'higher than' : 'lower than'} the state average. Income patterns reveal spatial disparities that affect access to climate solutions, transportation options, and resilience resources. Understanding income distribution helps identify communities that may need targeted assistance for climate action programs.
                  </p>
                ) : peopleMapMetrics.variable === 'political_lean' ? (
                  <p>
                    {currentLocation} has a political lean of {peopleMapMetrics.currentValue > 0 ? `D +${peopleMapMetrics.currentValue.toFixed(1)}` : peopleMapMetrics.currentValue < 0 ? `R +${Math.abs(peopleMapMetrics.currentValue).toFixed(1)}` : 'Even'}, compared to the California average of {peopleMapMetrics.californiaAverage > 0 ? `D +${peopleMapMetrics.californiaAverage.toFixed(1)}` : peopleMapMetrics.californiaAverage < 0 ? `R +${Math.abs(peopleMapMetrics.californiaAverage).toFixed(1)}` : 'Even'}. {peopleMapMetrics.currentValue > peopleMapMetrics.californiaAverage
                      ? `${currentLocation} has stronger Democratic leanings than the state average, suggesting potential support for climate action policies.`
                      : `${currentLocation} has weaker Democratic leanings than the state average, highlighting the importance of bipartisan climate solutions.`} Political leanings data helps inform tailored climate communication strategies.
                  </p>
                ) : (
                  <p>
                    {currentLocation} has a population density of {Math.round(peopleMapMetrics.currentValue).toLocaleString()} persons per square mile, compared to the California average of {Math.round(peopleMapMetrics.californiaAverage).toLocaleString()} persons per square mile—{peopleMapMetrics.currentValue > peopleMapMetrics.californiaAverage ? 'higher than' : 'lower than'} the state average. Population density patterns influence transportation demand, infrastructure needs, and per-capita resource consumption. Denser areas often have lower per-capita emissions due to more efficient land use and transportation options. Understanding density patterns helps inform compact development strategies and transit-oriented planning.
                  </p>
                )}
              </OverviewContent>
            ) : undefined
          }
          relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description={
              peopleMapMetrics?.variable === 'income'
                ? "Household income directly influences access to climate solutions and resilience resources."
                : peopleMapMetrics?.variable === 'political_lean'
                ? "How political leanings data informs climate action planning and policy development."
                : "Population density directly impacts transportation emissions, infrastructure efficiency, and resource consumption patterns."
            }
          >
            {peopleMapMetrics?.variable === 'income' ? (
              <div className="methodology-details-simple">
                <h5>Technology Access</h5>
                <p>Higher-income households can more readily invest in energy-efficient appliances, electric vehicles, and home electrification. Lower-income communities face barriers to clean technology adoption and may need financial assistance programs.</p>
              </div>
            ) : peopleMapMetrics?.variable === 'political_lean' ? (
              <>
                <div className="methodology-details-simple">
                  <h5>Policy Support</h5>
                  <p>Political leanings data helps identify communities that may be more or less supportive of climate action policies, enabling tailored communication strategies.</p>
                </div>
                <div className="methodology-details-simple">
                  <h5>Coalition Building</h5>
                  <p>Understanding political preferences helps identify opportunities for bipartisan climate solutions and effective policy implementation.</p>
                </div>
              </>
            ) : (
              <>
                <div className="methodology-details-simple">
                  <h5>Transportation Efficiency</h5>
                  <p>Higher-density areas typically have lower per-capita transportation emissions due to shorter trip distances and better transit access.</p>
                </div>
                <div className="methodology-details-simple">
                  <h5>Infrastructure Planning</h5>
                  <p>Population density informs transit planning, active transportation infrastructure, and compact development strategies that reduce emissions.</p>
                </div>
              </>
            )}
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description={
              peopleMapMetrics?.variable === 'income'
                ? "Income disparities create barriers to climate action participation and increase vulnerability to climate impacts."
                : peopleMapMetrics?.variable === 'political_lean'
                ? "Equity implications and considerations for political leanings data in climate planning."
                : "Population density patterns can reflect historical development patterns and influence access to services and opportunities."
            }
          >
            {peopleMapMetrics?.variable === 'income' ? (
              <div className="methodology-details-simple">
                <h5>Access Barriers</h5>
                <p>Lower-income communities often face higher energy cost burdens and have limited resources for climate adaptation measures. Climate policies must include income-qualified programs and financial assistance to ensure equitable access to clean technologies.</p>
              </div>
            ) : peopleMapMetrics?.variable === 'political_lean' ? (
              <div className="methodology-details-simple">
                <h5>Policy Design</h5>
                <p>Climate policies should be designed to work across political divides and ensure benefits reach all communities, regardless of political preferences.</p>
              </div>
            ) : (
              <div className="methodology-details-simple">
                <h5>Service Access</h5>
                <p>Density patterns may reflect historical zoning and development policies that created inequitable access to services. Equitable climate action requires ensuring all density levels have access to clean transportation options and efficient infrastructure.</p>
              </div>
            )}
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description={
              peopleMapMetrics?.variable === 'income'
                ? "Median household income data from the American Community Survey with geographic visualization."
                : peopleMapMetrics?.variable === 'political_lean'
                ? "Political Lean analysis based on American Community Survey data with geographic visualization."
                : "Population density calculated from census tract population and land area data."
            }
            dataSources={
              peopleMapMetrics?.variable === 'income'
                ? [
                    'American Community Survey 5-Year Estimates (ACS5)',
                    'U.S. Census Bureau demographic data',
                    'California census tract boundaries',
                    'Median household income (B19013_001E)'
                  ]
                : peopleMapMetrics?.variable === 'political_lean'
                ? [
                    'American Community Survey 5-Year Estimates (ACS5)',
                    'U.S. Census Bureau demographic data',
                    'California census tract boundaries',
                    'Political lean estimates (Democrat minus Republican)'
                  ]
                : [
                    'American Community Survey 5-Year Estimates (ACS5)',
                    'U.S. Census Bureau population data',
                    'Census tract boundaries and land area',
                    'Population density = population / land area (square miles)'
                  ]
            }
            methodology={
              peopleMapMetrics?.variable === 'income'
                ? [
                    'Data represents median household income for each census tract',
                    'Geographic visualization shows income distribution across census tracts',
                    'Comparison calculated relative to California state average',
                    '5-year estimates provide more reliable data for smaller geographic areas',
                    'Income values adjusted for inflation and reported in current dollars'
                  ]
                : peopleMapMetrics?.variable === 'political_lean'
                ? [
                    'Political lean: Democrat minus Republican vote share in recent elections',
                    'Data represents the political orientation of each census tract',
                    'Geographic visualization shows political preferences across census tracts',
                    'Comparison calculated relative to California state average',
                    '5-year estimates provide more reliable data for smaller geographic areas',
                    'Based on recent federal and state election results'
                  ]
                : [
                    'Population data from ACS 5-year estimates for each census tract',
                    'Land area calculated from census tract boundary geometries',
                    'Density calculated as persons per square mile',
                    'Geographic visualization shows density distribution across census tracts',
                    'Comparison calculated relative to California state average',
                    '5-year estimates provide more reliable data for smaller geographic areas'
                  ]
            }
          />
        }
        activeView={activePeopleMapView}
        onViewChange={(view: string) =>
          setActivePeopleMapView(view as 'overview' | 'relevance' | 'equity' | 'methodology')
        }
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        appendLocationToTitle={true}
      />
      </SectionWrapper>

      <CustomSectionsForPage pageId="people" locationName={currentLocation} />
      <CustomDatasetsForPage pageId="people" locationName={currentLocation} />
    </div>
  );
};
